import React, { memo, useCallback, useLayoutEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'
import { connect, ConnectedProps, useSelector } from 'react-redux'
import { useLocation, useNavigate, useOutletContext } from 'react-router'

import { addMinutes } from 'date-fns'
import { format, zonedTimeToUtc } from 'date-fns-tz'
import { cloneDeep, isEmpty, isEqual, isNil } from 'lodash-es'
import { AnyAction, bindActionCreators, Dispatch } from 'redux'
import styled, { css, useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { LTSessionTypes } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/constants'
import { attendanceValues } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionConstants'
import { getLTSessionTypeAndRate } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import {
  getDbtTeensSchedulingAttendees,
  getDbtTeensSchedulingParticipantNames,
} from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils/dbtTeensScheduling'
import { InfractionStatus } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { FieldSchema, FieldValue } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tertiaryButton/TertiaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { InlineInfoBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineInfoBanner/InlineInfoBanner'
import { FormBody, FormBodyCustomStyles } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/formBody/FormBody'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { getNoShowLateCancelModality } from './getNoShowLateCancelModality'
import sessionCancelationMetadata from './sessionCancelationMetadata'
import sessionCancelationMetadataDA from './sessionCancelationMetadataDA'
import { actions, mixpanelEvents } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { meetingFormatChargeModalityMap, programConfig as PROGRAM_CONFIG } from '../../common/constants/appConstants'
import { actionAlertHandler, actionStyles } from '../../common/constants/reduxConstants'
import { CLIENTS_SESSIONS, LC_CALENDAR_CURRENT } from '../../common/constants/routingConstants'
import { submitRequestPaymentForm } from '../../common/http/data/payments'
import { getBCCharge, isEngageDAProvider, resolveDAEngageProgramName } from '../../common/utils/utils'
import { getAuthRoles, getAuthUser, getAuthUserId } from '../../data/auth/authSelectors'
import { getClientAppointmentDetails } from '../../data/lyraTherapy/clientSelectors'
import {
  getRequestPaymentDiffPricingEnabled,
  getRequestPaymentDiffPricingModifier,
  getRequestPaymentRates,
} from '../../data/requestPayment/requestPaymentSelectors'
import { updateEndpointCache } from '../../data/rtkQuery'
import { useAppDispatch } from '../../data/store'
import { usePreviousRoute } from '../../hooks/usePreviousRoute'
import { getProviderDetailsAddresses } from '../../providers/data/providerSelectors'
import appointmentsApi, {
  useCancelAppointmentMutation,
  useUpdateAppointmentMutation,
} from '../clients/clientDetails/data/appointmentsApi'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { setToastContent } from '../data/ltToastAutoActions'
import { getLTVideoSessionOpen } from '../data/ltVideoSelectors'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'

const Container = styled.View<{ theme: ThemeType }>(({ theme: { spacing, colors, breakpoints } }) => ({
  ...(!breakpoints.isMinWidthTablet && { padding: `${spacing['24px']} ${spacing['48px']}` }),
  maxWidth: '624px',
  margin: 'auto',
  marginTop: spacing['32px'],
  backgroundColor: colors.backgroundPrimary,
}))

const CancelHeadingNav = styled.View<{ theme: ThemeType; videoSessionOpen: boolean }>(
  ({ theme: { spacing, colors }, videoSessionOpen }) => ({
    marginTop: videoSessionOpen ? '120px' : spacing['16px'],
    paddingLeft: spacing['16px'],
    width: '100%',
    borderBottomStyle: 'solid',
    borderBottomWidth: '1px',
    borderBottomColor: colors.borderDefault,
    display: 'flex',
    flexDirection: 'row',
  }),
)

const CancelHeadingLeftContainer = styled.View({
  width: '50%',
})

const Subheader = styled(Title)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginBottom: spacing['24px'],
}))

const AppointmentInfo = styled(BodyText)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginBottom: spacing['24px'],
}))

const AdjacentButtonContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginLeft: 'auto',
  right: 0,
  flexDirection: 'row',
  paddingBottom: spacing['16px'],
  marginTop: spacing['8px'],
}))

const SubmitButton = styled(PrimaryButton)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginRight: spacing['16px'],
  marginLeft: spacing['16px'],
}))

type CancellationFormValues = {
  changeReason?: string
  changeNotice?: string
  changeNoticeDetail?: string
  providerInitiated?: string
  providerInitiatedDetails?: string
  technicalIssuesCancelDetails?: string
  technicalIssuesCancelSelection?: string
}

const SessionCancellation: React.FC<SessionCancellationProps> = ({ actions: { setToastContent } }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { setShowSubNav } = useOutletContext<{ setShowSubNav: (visibility: boolean) => void }>()
  useLayoutEffect(() => {
    setShowSubNav(false)
  }, [setShowSubNav])
  const { isDBTTeensEnabled, shouldUseInternationalCoachingSesssionType } = useFlags()
  const { formatMessage } = useIntl()
  const { colors } = useTheme()
  const [disabled, setDisabled] = useState(true)
  const [loading, setLoading] = useState(false)
  const [formValues, setFormValues] = useState<CancellationFormValues>({})
  const appointment = useSelector(getClientAppointmentDetails)

  const userId = useSelector(getAuthUserId)
  const userRoles = useSelector(getAuthRoles)
  const user = useSelector(getAuthUser)
  const videoSessionOpen = useSelector(getLTVideoSessionOpen)
  const { data: clientDetails } = useSelectedClientData()
  const sessionsRates = useSelector(getRequestPaymentRates)
  const differentialPricingEnabled = useSelector(getRequestPaymentDiffPricingEnabled)
  const differentialPricingModifier = useSelector(getRequestPaymentDiffPricingModifier)
  const appointmentStart = zonedTimeToUtc(
    new Date(`${appointment?.startDate} ${appointment?.startTime}`),
    appointment?.timeZone ?? 'America/Los_Angeles',
  )
  const appointmentEnd = addMinutes(appointmentStart, appointment?.appointmentDuration ?? 50)
  const appointmentDate = `${format(appointmentStart, 'EE MMM d y h:mm')} - ${format(appointmentEnd, 'h:mm a zzz')}`

  const inLateCancelWindow = (appointmentStart.getTime() - Date.now()) / (1000 * 60 * 60) <= 24
  const intl = useIntl()
  const [cancelAppointment] = useCancelAppointmentMutation()
  const [updateAppointment] = useUpdateAppointmentMutation()
  const prevRoute = usePreviousRoute()
  const { data: clientDetailsSelectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const programConfig = clientDetailsSelectedEpisode
    ? PROGRAM_CONFIG[clientDetailsSelectedEpisode.program_name]
    : undefined
  const isDBTTeensSession =
    isDBTTeensEnabled && clientDetailsSelectedEpisode?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens
  const { teenName, caregiverName } = getDbtTeensSchedulingParticipantNames({
    client: clientDetails,
    episode: clientDetailsSelectedEpisode,
    appointments: appointment ? [appointment] : [],
  })
  const dbtTeensSessionType =
    appointment?.sessionType ?? (appointment?.sessionNumber === 1 ? LTSessionTypes.DBT_TEENS_INTAKE_SESSION : undefined)
  const dbtTeensNotificationRecipientNames = getDbtTeensSchedulingAttendees({
    sessionType: dbtTeensSessionType,
    teenName,
    caregiverName,
  }).map(({ name }) => name)
  const notificationRecipientNames =
    isDBTTeensSession && dbtTeensNotificationRecipientNames.length > 0
      ? dbtTeensNotificationRecipientNames
      : [clientDetails?.first_name]
  const notificationRecipients = notificationRecipientNames.filter(Boolean).join(' and ')
  const providerAddresses = useSelector(getProviderDetailsAddresses)

  const getNavigationTarget = () => {
    if (location?.state?.returnRoute) {
      return location.state.returnRoute
    }
    if (prevRoute?.includes('calendar')) {
      return LC_CALENDAR_CURRENT.route
    }
    return CLIENTS_SESSIONS.route
  }

  const navigateToTarget = () => {
    setShowSubNav(true)
    navigate(getNavigationTarget())
  }

  const closeFunc = () => {
    setDisabled(true)
    navigateToTarget()
  }

  const modifyAppointment = async () => {
    const {
      changeReason,
      changeNotice,
      changeNoticeDetail,
      providerInitiated,
      providerInitiatedDetails,
      technicalIssuesCancelDetails,
      technicalIssuesCancelSelection,
    } = formValues
    const appointmentChangeReason = (
      changeReason !== 'clientCancel'
        ? changeReason
        : changeNotice === 'clientInitiated' || isEngageDAProvider(userRoles)
          ? changeNotice
          : changeNoticeDetail
    ) as string | undefined
    let appointmentCancelled
    if (
      appointment &&
      (technicalIssuesCancelDetails || technicalIssuesCancelSelection || providerInitiated || providerInitiatedDetails)
    ) {
      const appointmentToUpdate = cloneDeep(appointment)
      appointmentToUpdate.appointmentStatus = 'canceled'
      appointmentToUpdate.changeReason = appointmentChangeReason
      appointmentToUpdate.appointmentAttributes = {
        technicalIssuesCancelDetails,
        technicalIssuesCancelSelection,
        providerInitiatedCancelSelection: providerInitiated,
        providerInitiatedCancelDetails: providerInitiatedDetails,
      }
      appointmentCancelled = updateAppointment({
        data: appointmentToUpdate,
        providerId: userId,
      })
    } else {
      appointmentCancelled = cancelAppointment({
        providerId: userId,
        appointmentId: appointment!.appointmentId,
        changeReason: appointmentChangeReason,
      })
    }
    return appointmentCancelled.unwrap()
  }

  const cancelClicked = async () => {
    let appointmentCancelled
    let infractionStatusOverride: InfractionStatus | undefined
    try {
      const { changeReason, changeNotice } = formValues

      const isDAEngageProvider = user?.roles && isEngageDAProvider(user.roles)
      const isInsufficientNoticeClientCancellation =
        changeReason === 'clientCancel' && changeNotice === 'clientInitiatedLate'

      if (
        isDAEngageProvider &&
        isInsufficientNoticeClientCancellation &&
        appointment &&
        clientDetails &&
        clientDetailsSelectedEpisode
      ) {
        const cancellationFormValues = {
          attendance: attendanceValues.LAST_MIN_CANCEL,
          changeReason: 'clientCancel',
          changeNotice: 'clientInitiatedLate',
          patient_date_of_birth: clientDetails.date_of_birth,
          modality: getNoShowLateCancelModality(appointment.meetingFormat, providerAddresses),
          diagnosis: null,
        }
        const sessionRate =
          clientDetailsSelectedEpisode && clientDetailsSelectedEpisode.program_name
            ? getLTSessionTypeAndRate({
                // CPR-2213: resolve LCT episodes to DirectAccessIndividualTherapy for DA Engage
                // providers, consistent with EndOfSession / useCloseSessionEligibility.
                programName: resolveDAEngageProgramName(
                  clientDetailsSelectedEpisode.program_name,
                  Boolean(isDAEngageProvider),
                ),
                sessionNumber: appointment.sessionNumber,
                sessionsRates: sessionsRates ?? {},
                dateOfBirth: clientDetails.date_of_birth ?? '',
                modality: meetingFormatChargeModalityMap[
                  appointment.meetingFormat as keyof typeof meetingFormatChargeModalityMap
                ] as string,
                differentialPricingEnabled: differentialPricingEnabled ?? false,
                differentialPricingModifier: differentialPricingModifier ?? 0,
                isInternationalCoachingSessionTypeEnabled: shouldUseInternationalCoachingSesssionType,
                clientCountry: clientDetails?.country,
                suggestedSessionType: appointment.suggestedSessionType,
              })
            : null

        await submitRequestPaymentForm(
          getBCCharge({
            values: cancellationFormValues,
            provider: user,
            client: clientDetails,
            appt: appointment,
            episode: clientDetailsSelectedEpisode,
            meetingFormat: appointment.meetingFormat,
            isProviderEngageDA: true,
            sessionRate: sessionRate?.sessionRate ?? '0',
            sessionType: sessionRate?.sessionType ?? 'other',
          }),
        )
        infractionStatusOverride = 'covered'
      }
      appointmentCancelled = await modifyAppointment()
    } catch (error) {
      actionAlertHandler({
        actionStyle: actionStyles.ERROR,
        message: error,
        expires: false,
        dispatch,
      })
      const errorType = error.response?.data?.type
      if (
        error.response?.status === 400 &&
        (errorType === 'cancellation_policy' || errorType === 'cancellation_policy_health_plan')
      ) {
        appointmentCancelled = await modifyAppointment()
        // Only default to 'client_owed' if no override was already set.
        // For DA Engage providers, the try block may have already set the override
        // to 'covered' after a successful payment form submission.
        if (appointment && !infractionStatusOverride) {
          infractionStatusOverride = 'client_owed'
        }
      }
    }
    return { appointmentCancelled, infractionStatusOverride }
  }

  const metadata =
    clientDetails &&
    (isEngageDAProvider(userRoles)
      ? sessionCancelationMetadataDA(clientDetails.first_name)
      : sessionCancelationMetadata(clientDetails.first_name, appointmentDate, inLateCancelWindow))

  const customValuesListener = useCallback(
    ({ values, errors }: { values: { [key: string]: FieldValue }; errors: any }) => {
      if (!isNil(values) && !isEmpty(values) && !isNil(errors) && isEmpty(errors)) {
        setFormValues((prevValues) => {
          if (!isEqual(prevValues, values)) {
            return values
          }
          return prevValues
        })
        setDisabled(false)
      } else {
        setDisabled(true)
      }
    },
    [],
  )

  const handleSubmit = async () => {
    setLoading(true)
    track({
      event: mixpanelEvents.BUTTON_PRESS,
      action: actions.CANCEL_SESSION,
      details: {
        appointmentId: appointment?.appointmentId,
        entryPoint: prevRoute?.includes('calendar') ? 'In Product Calendar' : 'Sessions tab',
      },
    })
    track({
      event: mixpanelEvents.CONFIRM_CANCEL_SESSION_PRIOR_TO_START,
    })
    const { infractionStatusOverride } = await cancelClicked()
    setDisabled(true)
    if (appointment && infractionStatusOverride) {
      dispatch(
        updateEndpointCache(appointmentsApi, 'getAppointments')
          .items({ appointmentId: appointment.appointmentId })
          .build((appt) => {
            appt.infractionStatus = infractionStatusOverride
          }),
      )
    }
    navigateToTarget()
    setTimeout(() => {
      setLoading(false)
      setToastContent({
        text: formatMessage(
          {
            defaultMessage: `Session {sessionNumber} canceled successfully`,
            description: 'A toast message letting the user know their session was canceled',
          },
          { sessionNumber: appointment?.sessionNumber },
        ),
        toastType: 'success',
        id: 'SessionCancellation-success',
      })
    }, 500)
  }
  if (!clientDetails || !metadata) {
    return null
  }

  if (loading) {
    return <LoadingIndicator size={45} />
  }
  return (
    <>
      <CancelHeadingNav videoSessionOpen={videoSessionOpen} testID={tID('SessionCancellation-heading-nav')}>
        <CancelHeadingLeftContainer>
          <Subheader
            size={TitleSize.LARGE}
            text={`Cancel Session ${appointment?.sessionNumber} with ${clientDetails.first_name}`}
            color={colors.textPrimary}
            style={{ marginBottom: 4 }}
          />
          <AppointmentInfo
            testID={tID('SessionCancellation-appointmentDate')}
            color={colors.textInactive}
            text={appointmentDate}
          />
        </CancelHeadingLeftContainer>
        <AdjacentButtonContainer>
          <TertiaryButton
            text='Cancel'
            onPress={() => closeFunc()}
            style={{
              borderWidth: 0,
              display: 'flex',
              height: 48,
              flexDirection: 'row',
              alignItems: 'center',
            }}
          />
          <SubmitButton
            testID={tID('SessionCancellation-Cancel')}
            text='Cancel Session'
            disabled={disabled}
            onPress={handleSubmit}
          />
        </AdjacentButtonContainer>
      </CancelHeadingNav>
      <Container testID={tID('SessionCancellation-container')}>
        {!isEngageDAProvider(userRoles) && (
          <InlineInfoBanner
            backgroundColor={colors.backgroundWarning}
            iconColor={colors.iconWarning}
            textColor={colors.textPrimary}
            text={
              <>
                You are about to cancel session {appointment?.sessionNumber} for {clientDetails.first_name}. This action
                has billing implications for clients and customers. Learn more about{' '}
                <Link
                  text={'program session closure policy.'}
                  onPress={() => window.open(programConfig?.sessionClosureGuide, '_blank')}
                  testID={tID(`SessionCancellation-sessionClosurePolicyLink`)}
                  size={BodyTextSize.DEFAULT}
                />
              </>
            }
          />
        )}
        <FormBody
          customValuesListener={customValuesListener}
          formContext={{ CustomLoadingIndicator: memo(() => <View />) }}
          intl={intl}
          name={'cancelSession'}
          saveForm={({ formSubmit }: { values: CancellationFormValues; formSubmit: boolean }) => {
            if (formSubmit) {
              cancelClicked()
            }
          }}
          schema={metadata.schema as FieldSchema}
          uiSchema={metadata.uiSchema}
          scrollContainerCustomStyles={{
            formBodyPageContainer: css`
              background-color: ${colors.backgroundPrimary};
              padding-top: 0px;
              max-width: 720px;
              margin: 0;
              padding: 0;
            `,
            scrollContainerStyle: {
              margin: 0,
              padding: 0,
            },
            scrollContainerContentCustomStyles: {
              padding: 0,
              margin: 0,
            },
          }}
          formBodyCustomStyles={
            {
              backgroundColor: colors.backgroundPrimary,
              submitButtonContainer: {
                backgroundColor: colors.backgroundPrimary,
                borderTopWidth: 0,
                borderWidth: '0 !important',
                borderColor: 'white',
                boxShadow: 'none',
                paddingLeft: 0,
              },
            } as unknown as FormBodyCustomStyles
          }
        >
          <AppointmentInfo
            size={BodyTextSize.SMALL}
            color={colors.textInactive}
            testID={tID('SessionCancellation-clientNotificationText')}
            text={`${notificationRecipients} will receive a notification that this appointment has been canceled.`}
          />
        </FormBody>
      </Container>
    </>
  )
}

type SessionCancellationProps = ConnectedProps<typeof connector>

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  actions: bindActionCreators(
    {
      setToastContent,
    },
    dispatch,
  ),
})

const connector = connect(null, mapDispatchToProps)

export default connector(SessionCancellation)
